<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">System Health & Monitoring</h1>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Monitor system health, services, and platform statistics
      </p>
    </div>

    <!-- Service Status Cards -->
    <div>
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Service Status</h2>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <!-- Database Status -->
        <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <CircleStackIcon
                  :class="serviceStatus.database ? 'text-green-500' : 'text-red-500'"
                  class="h-8 w-8"
                />
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Database
                  </dt>
                  <dd class="text-lg font-semibold" :class="serviceStatus.database ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                    {{ serviceStatus.database ? 'Online' : 'Offline' }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Cache Status -->
        <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <BoltIcon
                  :class="serviceStatus.cache ? 'text-green-500' : 'text-red-500'"
                  class="h-8 w-8"
                />
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Cache
                  </dt>
                  <dd class="text-lg font-semibold" :class="serviceStatus.cache ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                    {{ serviceStatus.cache ? 'Online' : 'Offline' }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Queue Status -->
        <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <QueueListIcon
                  :class="serviceStatus.queue ? 'text-green-500' : 'text-red-500'"
                  class="h-8 w-8"
                />
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Queue
                  </dt>
                  <dd class="text-lg font-semibold" :class="serviceStatus.queue ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                    {{ serviceStatus.queue ? 'Online' : 'Offline' }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Mail Status -->
        <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <EnvelopeIcon
                  :class="serviceStatus.mail ? 'text-green-500' : 'text-red-500'"
                  class="h-8 w-8"
                />
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Mail
                  </dt>
                  <dd class="text-lg font-semibold" :class="serviceStatus.mail ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                    {{ serviceStatus.mail ? 'Online' : 'Offline' }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Storage Status -->
        <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <ServerIcon
                  :class="serviceStatus.storage ? 'text-green-500' : 'text-red-500'"
                  class="h-8 w-8"
                />
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Storage
                  </dt>
                  <dd class="text-lg font-semibold" :class="serviceStatus.storage ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                    {{ serviceStatus.storage ? 'Writable' : 'Read-only' }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Storage Statistics -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
          Storage Statistics
        </h3>
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-4">
          <div>
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Quota</dt>
            <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {{ storageStats.total_quota_gb }} GB
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Used</dt>
            <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {{ storageStats.total_used_gb }} GB
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Available</dt>
            <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {{ storageStats.available_gb }} GB
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Usage</dt>
            <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {{ storageStats.usage_percentage }}%
            </dd>
          </div>
        </div>
        <div class="mt-4">
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
            <div
              class="h-4 rounded-full"
              :class="getStorageBarColor(storageStats.usage_percentage)"
              :style="{ width: storageStats.usage_percentage + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- System Configuration -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- System Information -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            System Information
          </h3>
          <dl class="space-y-3">
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">PHP Version</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ systemInfo.php_version }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Laravel Version</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ systemInfo.laravel_version }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Environment</dt>
              <dd class="text-sm">
                <span
                  :class="systemInfo.app_env === 'production' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ systemInfo.app_env }}
                </span>
              </dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Debug Mode</dt>
              <dd class="text-sm">
                <span
                  :class="systemInfo.app_debug ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ systemInfo.app_debug ? 'Enabled' : 'Disabled' }}
                </span>
              </dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">App URL</dt>
              <dd class="text-sm text-gray-900 dark:text-white truncate">{{ systemInfo.app_url }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Database Configuration -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Database Configuration
          </h3>
          <dl class="space-y-3">
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Connection</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ databaseInfo.connection }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Driver</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ databaseInfo.driver }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Database</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ databaseInfo.database }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Host</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ databaseInfo.host }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Cache Configuration -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Cache Configuration
          </h3>
          <dl class="space-y-3">
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Driver</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ cacheInfo.driver }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Prefix</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ cacheInfo.prefix || 'None' }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Queue Configuration -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Queue Configuration
          </h3>
          <dl class="space-y-3">
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Driver</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ queueInfo.driver }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Connection</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ queueInfo.connection || 'Default' }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <!-- Mail Configuration -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
          Mail Configuration
        </h3>
        <dl class="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div class="flex justify-between sm:block">
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Driver</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ mailInfo.driver }}</dd>
          </div>
          <div class="flex justify-between sm:block">
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">From Address</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ mailInfo.from_address }}</dd>
          </div>
          <div class="flex justify-between sm:block">
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">From Name</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ mailInfo.from_name }}</dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  CircleStackIcon,
  BoltIcon,
  QueueListIcon,
  EnvelopeIcon,
  ServerIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  systemInfo: {
    type: Object,
    required: true,
  },
  databaseInfo: {
    type: Object,
    required: true,
  },
  cacheInfo: {
    type: Object,
    required: true,
  },
  queueInfo: {
    type: Object,
    required: true,
  },
  mailInfo: {
    type: Object,
    required: true,
  },
  storageStats: {
    type: Object,
    required: true,
  },
  serviceStatus: {
    type: Object,
    required: true,
  },
});

// Get storage bar color based on usage percentage
const getStorageBarColor = (percentage) => {
  if (percentage >= 90) {
    return 'bg-red-600';
  } else if (percentage >= 75) {
    return 'bg-yellow-500';
  } else if (percentage >= 50) {
    return 'bg-blue-500';
  } else {
    return 'bg-green-500';
  }
};
</script>
